import { BroadcastStatus } from "apps/home/constants";
import { Flex } from "styled-system/jsx";
import { Icon, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { CheckCircleSolid, Clock, SystemRestart, XmarkCircle } from "iconoir-react";
import { DashedCircle } from "shared/components/atoms";

type BroadcastStatusIndicatorProps = {
  status: BroadcastStatus;
};

const COMPLETED_STATUSES = ["VIEWED", "COMPLETED", "DOWNLOADED", "PLAYED"];

function getStatusConfig(status: BroadcastStatus) {
  switch (status) {
    case "FORM_DRAFT":
      return {
        textColor: "text.highlight.mid" as const,
        iconColor: "icon.highlight.light" as const,
        iconElement: null,
        useDashedCircle: true,
      };
    case "FORM_SUBMITTED":
      return {
        textColor: "text.success.mid" as const,
        iconColor: "icon.success.mid" as const,
        iconElement: CheckCircleSolid,
        useDashedCircle: false,
      };
    case "FORM_PENDING_REVIEW":
      return {
        textColor: "text.warning.mid" as const,
        iconColor: "icon.warning.mid" as const,
        iconElement: Clock,
        useDashedCircle: false,
      };
    case "FORM_REJECTED":
      return {
        textColor: "text.error.mid" as const,
        iconColor: "icon.error.mid" as const,
        iconElement: XmarkCircle,
        useDashedCircle: false,
      };
    case "IN_PROGRESS":
      return {
        textColor: "text.highlight.mid" as const,
        iconColor: "icon.highlight.light" as const,
        iconElement: null,
        useDashedCircle: true,
      };
    default:
      if (COMPLETED_STATUSES.includes(status)) {
        return {
          textColor: "text.success.mid" as const,
          iconColor: "icon.success.mid" as const,
          iconElement: CheckCircleSolid,
          useDashedCircle: false,
        };
      }
      return {
        textColor: "text.accent.blue.mid" as const,
        iconColor: "icon.accent.blue.mid" as const,
        iconElement: SystemRestart,
        useDashedCircle: false,
      };
  }
}

function getStatusLabel(
  status: BroadcastStatus,
  tCore: (key: string) => string,
  tHome: (key: string) => string,
) {
  switch (status) {
    case "FORM_DRAFT":
      return tHome("FORM_STATUS_DRAFT");
    case "FORM_SUBMITTED":
      return tHome("FORM_STATUS_SUBMITTED");
    case "FORM_PENDING_REVIEW":
      return tHome("FORM_STATUS_PENDING_REVIEW");
    case "FORM_REJECTED":
      return tHome("FORM_STATUS_REJECTED");
    case "NOT_VIEWED":
      return tCore("NEW");
    default:
      return tCore(status);
  }
}

export const BroadcastStatusIndicator = ({
  status,
}: BroadcastStatusIndicatorProps) => {
  const { t: tCore } = useTranslation("core", {
    keyPrefix: "HOME_PAGE.FOR_YOU",
  });
  const { t: tHome } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const config = getStatusConfig(status);

  return (
    <Flex alignItems={"center"} gap={"75"}>
      <Text visual={"smallSemiBold"} color={config.textColor}>
        {getStatusLabel(status, tCore, tHome)}
      </Text>
      {config.useDashedCircle ? (
        <DashedCircle
          color={config.iconColor}
          width={"16px"}
          height={"16px"}
        />
      ) : (
        <Icon
          Element={config.iconElement!}
          color={config.iconColor}
          size={"sm"}
        />
      )}
    </Flex>
  );
};
